import { useEffect, useMemo, useState } from "react";
import {
  CalendarX,
  CheckCircle,
  Clock,
  Coins,
  RocketLaunch,
  Trash,
} from "@phosphor-icons/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Banner } from "@/components/Banner";
import { Button } from "@/components/Button";
import { useConfirm } from "@/components/Confirm";
import { CopyField } from "@/components/CopyButton";
import { EmailBlock } from "@/components/EmailBlock";
import { EventHero } from "@/components/event/EventHero";
import { ItemsSection } from "@/components/event/ItemsSection";
import { ParticipantsBar } from "@/components/event/ParticipantsBar";
import { ParticipantsList } from "@/components/event/ParticipantsList";
import { Input, Textarea } from "@/components/Input";
import { LinkSecurityHint, LinkSecurityNote } from "@/components/LinkSecurityNote";
import { ShareRow } from "@/components/ShareRow";
import { Skeleton } from "@/components/Skeleton";
import { StatsChart } from "@/components/StatsChart";
import { useApiErrorToast, useToast } from "@/components/Toast";
import { usePageMeta } from "@/hooks/usePageMeta";
import type { EventAdmin, Item } from "@/lib/api";
import { recentEventsStorage } from "@/lib/storage";
import {
  isoToLocalDate,
  isoToLocalTime,
  localDateTimeToIso,
} from "@/lib/deeplink";
import {
  useAddItem,
  useAddParticipant,
  useAdminEvent,
  useCancelEvent,
  useDeleteItem,
  useDeleteParticipant,
  useDeleteEvent,
  useDuplicateItem,
  useEventStats,
  usePurgeItem,
  useReorderItems,
  useRestoreItem,
  useStartUpgradeCheckout,
  useUncancelEvent,
  useUpdateEvent,
  useUpdateItem,
  useUpdateParticipant,
  useUpdateRecentEntry,
  type EventUpdatePayload,
} from "@/hooks/useAdminEvent";
import styles from "./AdminEvent.module.css";

const ADMIN_PANEL_KEY = "bm:admin-panel-open";

export function AdminEvent() {
  const params = useParams();
  const shortcut = params.shortcut ?? "";
  const adminKey = params.adminkey ?? "";
  const { t } = useTranslation();
  const { data: event, isLoading, error } = useAdminEvent(shortcut, adminKey);
  const saveRecent = useUpdateRecentEntry();
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (event) saveRecent(event);
  }, [event, saveRecent]);

  usePageMeta({
    title: event?.name ?? t("admin.kicker"),
    robots: "noindex,nofollow",
  });

  if (isLoading) return <Skeleton variant="page" />;
  if (error || !event) {
    const status = (error as { status?: number } | null)?.status;
    return (
      <section>
        <h1>{status === 404 || status === 403 ? t("join.notFoundTitle") : t("join.errorTitle")}</h1>
        <p>{status === 404 || status === 403 ? t("join.notFoundBody") : t("join.errorBody")}</p>
      </section>
    );
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const adminUrl = `${origin}/proadmin/${event.shortcut}/${event.adminKey}`;
  const guestUrl = `${origin}/view/${event.shortcut}`;
  const visibleItems = event.items.filter((i) => !i.deletedAt);
  const trashedItems = event.items.filter((i) => i.deletedAt);
  const canceled = event.canceledAt !== null;
  const now = Date.now();
  const deadlineTs = event.deadline ? new Date(event.deadline).getTime() : new Date(event.eventAt).getTime();
  const deadlinePassed = !canceled && deadlineTs < now;

  return (
    <section className={styles.page}>
      {editMode ? (
        <EventDetailsForm
          event={event}
          shortcut={shortcut}
          adminKey={adminKey}
          onClose={() => setEditMode(false)}
        />
      ) : (
        <EventHero
          event={event}
          role="admin"
          tier={event.tier}
          onEdit={() => setEditMode(true)}
          guestUrl={guestUrl}
        />
      )}

      {canceled && (
        <Banner variant="danger" icon={CalendarX} title={t("admin.canceledBanner")}>
          {event.cancelReason && t("admin.canceledReason", { reason: event.cancelReason })}
        </Banner>
      )}
      {deadlinePassed && (
        <Banner variant="warning" icon={Clock} title={t("admin.deadlinePassedTitle")}>
          {t("admin.deadlinePassedBody")}
        </Banner>
      )}

      <ParticipantsAdmin shortcut={shortcut} adminKey={adminKey} event={event} />

      <ItemsAdmin shortcut={shortcut} adminKey={adminKey} event={event} items={visibleItems} />

      <AdminPanel
        shortcut={shortcut}
        adminKey={adminKey}
        event={event}
        adminUrl={adminUrl}
        guestUrl={guestUrl}
        trashedItems={trashedItems}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Teilnehmer-Sektion (mit ParticipantsBar + Expand)
// ---------------------------------------------------------------------------

function ParticipantsAdmin({
  shortcut,
  adminKey,
  event,
}: {
  shortcut: string;
  adminKey: string;
  event: EventAdmin;
}) {
  const add = useAddParticipant(shortcut, adminKey);
  const update = useUpdateParticipant(shortcut, adminKey);
  const del = useDeleteParticipant(shortcut, adminKey);

  return (
    <ParticipantsBar
      participants={event.participants}
      detailsSlot={
        <ParticipantsList
          viewerRole="admin"
          participants={event.participants}
          onAdd={(payload) => add.mutate(payload)}
          onSave={(id, payload) => update.mutate({ id, ...payload })}
          onDelete={(id) => del.mutate(id)}
          isAddPending={add.isPending}
        />
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Mitbringsel-Sektion
// ---------------------------------------------------------------------------

function ItemsAdmin({
  shortcut,
  adminKey,
  items,
  event,
}: {
  shortcut: string;
  adminKey: string;
  items: Item[];
  event: EventAdmin;
}) {
  const add = useAddItem(shortcut, adminKey);
  const update = useUpdateItem(shortcut, adminKey);
  const del = useDeleteItem(shortcut, adminKey);
  const duplicate = useDuplicateItem(shortcut, adminKey);
  const reorder = useReorderItems(shortcut, adminKey);

  return (
    <ItemsSection
      viewerRole="admin"
      items={items}
      participants={event.participants}
      onAdd={(payload) => add.mutate(payload)}
      onEdit={(id, payload) => update.mutate({ id, ...payload })}
      onAssign={(id, participantId, forAllUsers) =>
        update.mutate({ id, participantId, forAllUsers })
      }
      onDuplicate={(id) => duplicate.mutate(id)}
      onDelete={(id) => del.mutate(id)}
      onReorder={(updates) => reorder.mutate(updates)}
      isAddPending={add.isPending}
    />
  );
}

// ---------------------------------------------------------------------------
// Admin-Verwalten-Panel (collapsible, Status im LocalStorage gemerkt)
// ---------------------------------------------------------------------------

function AdminPanel({
  shortcut,
  adminKey,
  event,
  adminUrl,
  guestUrl,
  trashedItems,
}: {
  shortcut: string;
  adminKey: string;
  event: EventAdmin;
  adminUrl: string;
  guestUrl: string;
  trashedItems: Item[];
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem(ADMIN_PANEL_KEY) !== "0";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(ADMIN_PANEL_KEY, open ? "1" : "0");
  }, [open]);

  const canceled = event.canceledAt !== null;

  return (
    <details className={styles.adminPanel} open={open}>
      <summary
        className={styles.adminPanelSummary}
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
      >
        <strong>{t("event.manageTitle")}</strong>
        <span className={styles.adminPanelHint}>{t("event.manageHint")}</span>
      </summary>
      <div className={styles.adminPanelBody}>
        <CopyField value={adminUrl} label={t("admin.yourAdminLink")} />
        <p className={styles.hint}>{t("admin.adminLinkHint")}</p>
        <CopyField value={guestUrl} label={t("admin.guestLink")} />
        <div className={styles.shareRowWrap}>
          <ShareRow url={guestUrl} title={event.name} eventName={event.name} />
          <LinkSecurityHint />
        </div>
        <LinkSecurityNote />
        <p className={styles.hint}>{limitsLabel(event, t)}</p>
        <EmailBlock event={event} shortcut={shortcut} adminKey={adminKey} />
        {event.tier === "free" && <UpgradeBlock shortcut={shortcut} adminKey={adminKey} />}
        {event.tier === "pro" && trashedItems.length > 0 && (
          <TrashSection shortcut={shortcut} adminKey={adminKey} items={trashedItems} />
        )}
        {event.tier === "pro" && <StatsSection shortcut={shortcut} adminKey={adminKey} />}
        <div className={styles.footerActions}>
          {canceled ? (
            <UncancelButton shortcut={shortcut} adminKey={adminKey} />
          ) : (
            <CancelButton shortcut={shortcut} adminKey={adminKey} />
          )}
          <DeleteEventButton shortcut={shortcut} adminKey={adminKey} />
        </div>
      </div>
    </details>
  );
}

function limitsLabel(
  event: { limits: { participantsUsed: number; participantsMax: number | null; itemsUsed: number; itemsMax: number | null } },
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  const l = event.limits;
  if (l.participantsMax === null && l.itemsMax === null) {
    return t("admin.limitsPro", { participantsUsed: l.participantsUsed, itemsUsed: l.itemsUsed });
  }
  return t("admin.limitsFree", {
    participantsUsed: l.participantsUsed,
    participantsMax: l.participantsMax,
    itemsUsed: l.itemsUsed,
    itemsMax: l.itemsMax,
  });
}

// ---------------------------------------------------------------------------
// Sub-Komponenten (übernommen aus altem AdminEvent)
// ---------------------------------------------------------------------------

function TrashSection({
  shortcut,
  adminKey,
  items,
}: {
  shortcut: string;
  adminKey: string;
  items: Item[];
}) {
  const { t } = useTranslation();
  const restore = useRestoreItem(shortcut, adminKey);
  const purge = usePurgeItem(shortcut, adminKey);
  const confirm = useConfirm();

  return (
    <section className={styles.trashSection}>
      <h3 className={styles.trashHeading}>
        <Trash size={18} weight="regular" /> {t("admin.itemsHeading")}
      </h3>
      <div className={styles.trashList}>
        {items.map((i) => (
          <div key={i.id} className={styles.trashRow}>
            <span>{i.name}</span>
            <div className={styles.trashRowActions}>
              <Button type="button" variant="ghost" size="compact" onClick={() => restore.mutate(i.id)}>
                <CheckCircle size={16} weight="regular" />
                {t("admin.restore" as never, { defaultValue: "Wiederherstellen" })}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="compact"
                onClick={async () => {
                  const r = await confirm({
                    title: t("admin.confirmPurgeTitle"),
                    body: t("admin.confirmPurgeBody"),
                    variant: "danger",
                    confirmLabel: t("common.delete"),
                  });
                  if (r.confirmed) purge.mutate(i.id);
                }}
              >
                <Trash size={16} weight="regular" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CancelButton({ shortcut, adminKey }: { shortcut: string; adminKey: string }) {
  const { t } = useTranslation();
  const cancel = useCancelEvent(shortcut, adminKey);
  const confirm = useConfirm();
  const toast = useToast();
  const apiError = useApiErrorToast();

  const onCancel = async () => {
    const r = await confirm({
      title: t("admin.cancelEvent"),
      body: t("admin.cancelBody"),
      variant: "danger",
      confirmLabel: t("admin.cancelEvent"),
      prompt: {
        label: t("admin.cancelReason"),
        placeholder: t("admin.cancelReasonPlaceholder"),
        multiline: true,
        maxLength: 500,
      },
    });
    if (!r.confirmed) return;
    try {
      await cancel.mutateAsync({ cancelReason: r.value?.trim() || undefined });
      toast.success(t("admin.cancelToast"));
    } catch (err) {
      apiError(err, t("common.errorGeneric"));
    }
  };

  return (
    <Button type="button" variant="danger" onClick={onCancel}>
      <CalendarX size={16} weight="regular" />
      {t("admin.cancelEvent")}
    </Button>
  );
}

function UncancelButton({ shortcut, adminKey }: { shortcut: string; adminKey: string }) {
  const { t } = useTranslation();
  const uncancel = useUncancelEvent(shortcut, adminKey);
  return (
    <Button type="button" variant="secondary" onClick={() => uncancel.mutate()}>
      <CheckCircle size={16} weight="regular" />
      {t("admin.uncancelEvent")}
    </Button>
  );
}

function DeleteEventButton({ shortcut, adminKey }: { shortcut: string; adminKey: string }) {
  const { t } = useTranslation();
  const del = useDeleteEvent(shortcut, adminKey);
  const confirm = useConfirm();
  const toast = useToast();
  const apiError = useApiErrorToast();
  const navigate = useNavigate();

  const onDelete = async () => {
    const r = await confirm({
      title: t("admin.deleteEventTitle"),
      body: t("admin.deleteEventBody"),
      variant: "danger",
      confirmLabel: t("admin.deleteEventConfirm"),
      prompt: {
        label: t("admin.deleteEventTypeToConfirm"),
        placeholder: t("admin.deleteEventKeyword"),
        required: true,
      },
    });
    if (!r.confirmed) return;
    if ((r.value ?? "").trim().toLowerCase() !== t("admin.deleteEventKeyword").toLowerCase()) {
      toast.error(t("admin.deleteEventKeywordMismatch"));
      return;
    }
    try {
      await del.mutateAsync();
      recentEventsStorage.remove(shortcut);
      toast.success(t("admin.deleteEventToast"));
      navigate("/", { replace: true });
    } catch (err) {
      apiError(err, t("common.errorGeneric"));
    }
  };

  return (
    <Button type="button" variant="ghost" onClick={onDelete} disabled={del.isPending}>
      <Trash size={16} weight="regular" />
      {t("admin.deleteEvent")}
    </Button>
  );
}

function EventDetailsForm({
  event,
  shortcut,
  adminKey,
  onClose,
}: {
  event: EventAdmin;
  shortcut: string;
  adminKey: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateEvent(shortcut, adminKey);

  const [name, setName] = useState(event.name);
  const [description, setDescription] = useState(event.description ?? "");
  const [location, setLocation] = useState(event.location ?? "");
  const [eventDate, setEventDate] = useState(isoToLocalDate(event.eventAt));
  const [eventTime, setEventTime] = useState(isoToLocalTime(event.eventAt));
  const [deadlineDate, setDeadlineDate] = useState(
    event.deadline ? isoToLocalDate(event.deadline) : "",
  );
  const [deadlineTime, setDeadlineTime] = useState(
    event.deadline ? isoToLocalTime(event.deadline) : "",
  );
  const [expandable, setExpandable] = useState(event.expandable);
  const [anonymous, setAnonymous] = useState(event.anonymous);
  const [error, setError] = useState<string | null>(null);

  const onSave = async () => {
    setError(null);
    const payload: EventUpdatePayload = {
      name: name.trim(),
      description: description.trim() || null,
      location: location.trim() || null,
      eventAt: localDateTimeToIso(eventDate, eventTime, event.timezone) || undefined,
      deadline:
        deadlineDate && deadlineTime
          ? localDateTimeToIso(deadlineDate, deadlineTime, event.timezone)
          : null,
      expandable,
      anonymous,
    };
    try {
      await update.mutateAsync(payload);
      onClose();
    } catch (err) {
      const e = err as { status?: number; message?: string };
      setError([e.status ? `HTTP ${e.status}` : null, e.message].filter(Boolean).join(" "));
    }
  };

  return (
    <div className={styles.editForm}>
      <Input
        label={t("newEvent.nameLabel")}
        required
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={100}
        autoFocus
      />
      <div className={styles.editRow}>
        <Input
          label={t("newEvent.eventDateLabel")}
          type="date"
          value={eventDate}
          onChange={(e) => setEventDate(e.target.value)}
        />
        <Input
          label={t("newEvent.eventTimeLabel")}
          type="time"
          step={1800}
          value={eventTime}
          onChange={(e) => setEventTime(e.target.value)}
        />
      </div>
      <Input
        label={t("newEvent.locationLabel")}
        value={location}
        onChange={(e) => setLocation(e.target.value)}
        maxLength={100}
      />
      <Textarea
        label={t("newEvent.descriptionLabel")}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={3}
      />
      <div className={styles.editRow}>
        <Input
          label={t("newEvent.deadlineLabel")}
          type="date"
          value={deadlineDate}
          onChange={(e) => setDeadlineDate(e.target.value)}
        />
        <Input
          label={t("newEvent.eventTimeLabel")}
          type="time"
          step={1800}
          value={deadlineTime}
          onChange={(e) => setDeadlineTime(e.target.value)}
          disabled={!deadlineDate}
        />
      </div>
      <label className={styles.toggleRow}>
        <input
          type="checkbox"
          checked={expandable}
          onChange={(e) => setExpandable(e.target.checked)}
        />
        <span>
          <strong>{t("admin.expandableLabel")}</strong>
          <br />
          <span className={styles.toggleHint}>{t("admin.expandableHint")}</span>
        </span>
      </label>
      <label className={styles.toggleRow}>
        <input
          type="checkbox"
          checked={anonymous}
          onChange={(e) => setAnonymous(e.target.checked)}
        />
        <span>
          <strong>{t("admin.anonymousLabel")}</strong>
          <br />
          <span className={styles.toggleHint}>{t("admin.anonymousHint")}</span>
        </span>
      </label>
      {error && <div className={styles.errorBox}>{error}</div>}
      <div className={styles.editActions}>
        <Button type="button" variant="secondary" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button type="button" onClick={onSave} disabled={update.isPending || !name.trim()}>
          {t("common.save")}
        </Button>
      </div>
    </div>
  );
}

function UpgradeBlock({ shortcut, adminKey }: { shortcut: string; adminKey: string }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const upgrade = useStartUpgradeCheckout(shortcut, adminKey);
  const [error, setError] = useState<string | null>(null);

  const banner = useMemo(() => {
    const state = searchParams.get("upgrade");
    if (state === "success") return "success" as const;
    if (state === "cancel") return "cancel" as const;
    return null;
  }, [searchParams]);

  const dismissBanner = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("upgrade");
    next.delete("session_id");
    setSearchParams(next, { replace: true });
  };

  const onUpgrade = async () => {
    setError(null);
    try {
      const { checkoutUrl } = await upgrade.mutateAsync();
      window.location.assign(checkoutUrl);
    } catch (err) {
      const e = err as { status?: number; message?: string };
      if (e.status === 503) {
        setError(t("admin.upgradeUnavailable"));
      } else {
        setError([e.status ? `HTTP ${e.status}` : null, e.message].filter(Boolean).join(" "));
      }
    }
  };

  return (
    <div className={styles.upgradeBlock}>
      {banner === "success" && (
        <Banner variant="success" icon={Coins} title={t("admin.upgradeSuccessTitle")}>
          <span>{t("admin.upgradeSuccessBody")}</span>
          <button type="button" className={styles.bannerDismiss} onClick={dismissBanner}>
            {t("common.ok")}
          </button>
        </Banner>
      )}
      {banner === "cancel" && (
        <Banner variant="warning" icon={Clock} title={t("admin.upgradeCanceledTitle")}>
          <span>{t("admin.upgradeCanceledBody")}</span>
          <button type="button" className={styles.bannerDismiss} onClick={dismissBanner}>
            {t("common.ok")}
          </button>
        </Banner>
      )}
      <Button type="button" onClick={onUpgrade} disabled={upgrade.isPending}>
        <RocketLaunch size={18} weight="regular" />
        {upgrade.isPending ? t("admin.upgradePending") : t("admin.upgradeCta")}
      </Button>
      <p className={styles.hint}>{t("admin.upgradeHint")}</p>
      {error && <div className={styles.errorBox}>{error}</div>}
    </div>
  );
}

function StatsSection({ shortcut, adminKey }: { shortcut: string; adminKey: string }) {
  const { t } = useTranslation();
  const stats = useEventStats(shortcut, adminKey, true);
  return (
    <section className={styles.statsSection}>
      <h3 className={styles.trashHeading}>{t("admin.statsHeading")}</h3>
      <p className={styles.hint}>{t("admin.statsSubtitle")}</p>
      {stats.isLoading && <p className={styles.hint}>{t("admin.statsLoading")}</p>}
      {stats.isError && <p className={styles.hint}>{t("admin.statsError")}</p>}
      {stats.data && <StatsChart days={stats.data.days} />}
    </section>
  );
}
